<template>
  <div class="renderPage dashboard-page animate-fade-in">

    <!-- ═══ HERO SECTION ═══ -->
    <div class="hero-card mb-5">
      <div class="hero-orb hero-orb-1"></div>
      <div class="hero-orb hero-orb-2"></div>
      <div class="hero-content d-flex align-center justify-space-between flex-wrap" style="gap:12px;">
        <div>
          <div class="greeting-pill mb-2">
            <v-icon size="13" color="white" class="mr-1">{{ greetingIcon }}</v-icon>
            <span>{{ greeting }}</span>
          </div>
          <h1 class="hero-name mb-0">{{ user.nombre }}</h1>
          <p class="hero-sub mb-0">Lottery BestOne &mdash; Panel de Control</p>
        </div>
        <div class="hero-clock">
          <div class="clock-time">{{ currentTime }}</div>
          <div class="clock-date">{{ currentDate }}</div>
        </div>
      </div>
    </div>

    <!-- ═══ KPI CARDS ═══ -->
    <v-row dense class="mb-2">
      <v-col cols="6" sm="3" v-for="(kpi, i) in kpiCards" :key="i">
        <v-card class="kpi-card rounded-xl pa-3 pa-sm-4 overflow-hidden" elevation="0">
          <div class="kpi-accent-bar" :style="{ background: kpi.accent }"></div>
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="kpi-icon-wrap" :style="{ background: kpi.iconBg }">
              <v-icon :color="kpi.iconColor" size="18">{{ kpi.icon }}</v-icon>
            </div>
            <v-progress-circular v-if="kpi.loading" indeterminate :color="kpi.iconColor" size="14" width="2" />
          </div>
          <div class="kpi-value" :class="kpi.valueClass">{{ kpi.value }}</div>
          <div class="kpi-label">{{ kpi.label }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- ═══ RESULTS + QUICK ACCESS ═══ -->
    <v-row dense class="mt-2">
      <v-col cols="12" md="8">
        <v-card class="glass-card rounded-xl overflow-hidden" style="min-height:220px;">
          <div class="d-flex align-center justify-space-between px-4 pt-4 pb-3">
            <div class="d-flex align-center">
              <div class="section-icon-pill mr-3">
                <v-icon color="white" size="14">mdi-trophy</v-icon>
              </div>
              <span class="section-title">Resultados de Hoy</span>
            </div>
            <v-btn text x-small color="primary" to="/resultados" class="font-weight-bold">
              Ver todos <v-icon right x-small>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
          <v-divider class="mx-4" style="opacity:0.08;" />
          <div class="pa-3">
            <div v-if="loadingResults" class="d-flex justify-center align-center py-6">
              <v-progress-circular indeterminate color="primary" size="36" />
            </div>
            <v-row dense v-else-if="lotteryList.length > 0">
              <v-col cols="4" sm="3" md="2" v-for="(item, idx) in sortedResults" :key="idx" class="pa-1">
                <div class="result-mini rounded-lg text-center pa-2">
                  <div class="result-name text-xxs font-weight-bold text-truncate mb-1">{{ item.lotteryName }}</div>
                  <div class="result-circle mx-auto mb-1">
                    <span class="result-num">{{ item.listResults[0].number }}</span>
                  </div>
                  <div class="text-xxs grey--text">{{ item.time }}</div>
                </div>
              </v-col>
            </v-row>
            <div v-else class="text-center py-6">
              <v-icon size="40" color="grey lighten-2">mdi-trophy-outline</v-icon>
              <p class="text-caption grey--text mt-2 mb-0">Sin resultados aún hoy</p>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="glass-card rounded-xl pa-4 h-100" style="min-height:220px;">
          <div class="d-flex align-center mb-3">
            <div class="section-icon-pill mr-3">
              <v-icon color="white" size="14">mdi-flash</v-icon>
            </div>
            <span class="section-title">Accesos Rápidos</span>
          </div>
          <div class="d-flex flex-column quick-links-wrap">
            <router-link
              v-for="link in quickLinks"
              :key="link.label"
              :to="link.path"
              class="quick-link-item rounded-xl mb-2 d-flex align-center px-3"
              style="text-decoration:none;"
            >
              <div class="quick-link-icon-wrap mr-3" :style="{ background: link.iconBg }">
                <v-icon size="17" :style="{ color: link.iconHex }">{{ link.icon }}</v-icon>
              </div>
              <span class="quick-link-label font-weight-bold">{{ link.label }}</span>
              <v-spacer />
              <v-icon size="14" color="grey">mdi-chevron-right</v-icon>
            </router-link>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- ═══ CHART ═══ -->
    <v-row dense class="mt-2" v-if="chartReady">
      <v-col cols="12">
        <v-card class="glass-card rounded-xl overflow-hidden">
          <div class="d-flex align-center px-4 pt-4 pb-3">
            <div class="section-icon-pill mr-3">
              <v-icon color="white" size="14">mdi-chart-bar</v-icon>
            </div>
            <span class="section-title">Histórico de Ventas</span>
          </div>
          <v-divider class="mx-4" style="opacity:0.08;" />
          <div class="px-4 pb-4 pt-2">
            <Bar :chart-options="chartOptions" :chart-data="chartData" :height="200" />
          </div>
        </v-card>
      </v-col>
    </v-row>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import { Bar } from "vue-chartjs/legacy";
import Snackbar from "./shared/Snackbar.vue";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

export default {
  name: "Dashboard",
  components: { Bar, Snackbar },
  data() {
    return {
      currentTime: "",
      currentDate: "",
      clockInterval: null,
      loadingResults: false,
      lotteryList: [],
      loadingKpis: false,
      loadingUser: true,
      reportCedula: "",
      reportLabel: "",
      isAdmin: false,
      todaySales: "—",
      todayPrizes: "—",
      todayNet: "—",
      chartReady: false,
      chartData: {},
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(25,118,210,0.92)",
            titleColor: "#fff",
            bodyColor: "#fff",
            padding: 10,
            cornerRadius: 8,
          },
        },
        scales: {
          y: {
            grid: { color: "rgba(0,0,0,0.04)", drawBorder: false },
            ticks: { font: { size: 11 } },
          },
          x: {
            grid: { display: false },
            ticks: { font: { size: 11 } },
          },
        },
      },
      snackText: "",
      snackValue: false,
      quickLinks: [
        { label: "Vender Tiempos",    path: "/tiempos",              icon: "mdi-ticket-percent", iconBg: "rgba(25,118,210,0.12)",  iconHex: "#1976D2" },
        { label: "Ver Resultados",    path: "/resultados",           icon: "mdi-trophy",          iconBg: "rgba(255,160,0,0.12)",   iconHex: "#F9A825" },
        { label: "Canjear Premios",   path: "/canjear",              icon: "mdi-gift",            iconBg: "rgba(67,160,71,0.12)",   iconHex: "#388E3C" },
        { label: "Control de Ventas", path: "/reportecontrolventas", icon: "mdi-chart-line",      iconBg: "rgba(0,150,136,0.12)",   iconHex: "#00897B" },
        { label: "Buscar Tiquete",    path: "/buscartiquete",        icon: "mdi-magnify",         iconBg: "rgba(103,58,183,0.12)",  iconHex: "#673AB7" },
      ],
    };
  },

  computed: {
    ...mapGetters(["user", "strCia"]),

    sortedResults() {
      return [...this.lotteryList].reverse().slice(0, 6);
    },

    greeting() {
      const h = new Date().getHours();
      if (h < 12) return "Buenos días";
      if (h < 19) return "Buenas tardes";
      return "Buenas noches";
    },

    greetingIcon() {
      const h = new Date().getHours();
      if (h < 12) return "mdi-weather-sunny";
      if (h < 19) return "mdi-weather-partly-cloudy";
      return "mdi-weather-night";
    },

    kpiCards() {
      return [
        {
          label: "Ventas Hoy",
          value: this.todaySales,
          icon: "mdi-cash-multiple",
          iconColor: "primary",
          iconBg: "rgba(25,118,210,0.12)",
          accent: "linear-gradient(90deg,#1976D2,#42A5F5)",
          valueClass: "primary--text",
          loading: this.loadingKpis,
        },
        {
          label: "Premios Hoy",
          value: this.todayPrizes,
          icon: "mdi-trophy-outline",
          iconColor: "error",
          iconBg: "rgba(229,57,53,0.10)",
          accent: "linear-gradient(90deg,#E53935,#EF9A9A)",
          valueClass: "error--text",
          loading: this.loadingKpis,
        },
        {
          label: "Neto Hoy",
          value: this.todayNet,
          icon: "mdi-trending-up",
          iconColor: "success",
          iconBg: "rgba(67,160,71,0.10)",
          accent: "linear-gradient(90deg,#43A047,#A5D6A7)",
          valueClass: "success--text",
          loading: this.loadingKpis,
        },
        {
          label: "Reporte",
          value: this.reportLabel || "...",
          icon: this.isAdmin ? "mdi-account-group" : "mdi-account",
          iconColor: "deep-purple",
          iconBg: "rgba(103,58,183,0.10)",
          accent: "linear-gradient(90deg,#673AB7,#CE93D8)",
          valueClass: "deep-purple--text text-truncate",
          loading: this.loadingUser,
        },
      ];
    },
  },

  created() {
    this.updateClock();
    this.clockInterval = setInterval(this.updateClock, 1000);
    this.getResults();
    this.getUsuariosPorCia(); // determina admin/no-admin y luego llama getTodaySales
    this.getChartData();
  },

  beforeDestroy() {
    if (this.clockInterval) clearInterval(this.clockInterval);
  },

  methods: {
    updateClock() {
      const now = new Date();
      const cr = new Date(now.toLocaleString("en-US", { timeZone: "America/Costa_Rica" }));
      this.currentTime = cr.toLocaleTimeString("es-CR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      this.currentDate = cr.toLocaleDateString("es-CR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    },

    getResults() {
      this.loadingResults = true;
      axios
        .get("api/Tiempos/GetLotteryResultList")
        .then((r) => { this.lotteryList = r.data; })
        .catch((e) => console.log(e))
        .finally(() => { this.loadingResults = false; });
    },

    getTodaySales() {
      this.loadingKpis = true;
      const cr = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Costa_Rica" }));
      const y = cr.getFullYear();
      const m = String(cr.getMonth() + 1).padStart(2, "0");
      const d = String(cr.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;

      axios
        .get(`api/Tiempos/GetReporteControlVentas?idUsuario=${this.reportCedula}&fechaInicio=${dateStr}&fechaFin=${dateStr}&companyId=${this.strCia}`)
        .then((r) => {
          if (r.data && r.data.length > 0) {
            const row = r.data[0];
            this.todaySales = `₡${row.venta}`;
            this.todayPrizes = `₡${row.premios}`;
            this.todayNet = `₡${row.totalAdmin}`;
          } else {
            this.todaySales = "₡0";
            this.todayPrizes = "₡0";
            this.todayNet = "₡0";
          }
        })
        .catch(() => {
          this.todaySales = "—";
          this.todayPrizes = "—";
          this.todayNet = "—";
        })
        .finally(() => { this.loadingKpis = false; });
    },

    getUsuariosPorCia() {
      this.loadingUser = true;
      axios
        .get(`api/Usuarios/getUsuariosPorCia?companyId=${this.strCia}`)
        .then((r) => {
          const myUser = r.data.find((u) => u.cedula === this.user.cedula);
          if (myUser && myUser.tipo === true) {
            // Admin → ve todos los usuarios
            this.isAdmin = true;
            this.reportCedula = "0";
            this.reportLabel = "General";
          } else {
            // No admin → solo sus propios datos
            this.isAdmin = false;
            this.reportCedula = this.user.cedula;
            this.reportLabel = myUser ? myUser.nombre : this.user.nombre;
          }
        })
        .catch(() => {
          // Si falla, usamos la cédula propia como fallback
          this.reportCedula = this.user.cedula;
          this.reportLabel = this.user.nombre;
        })
        .finally(() => {
          this.loadingUser = false;
          this.getTodaySales(); // ahora sí tenemos reportCedula listo
        });
    },

    getChartData() {
      axios
        .get(`/api/General/getDashboardFacturasInfo?cia=${this.strCia}`)
        .then((r) => {
          if (r.data && r.data.datasets && r.data.datasets.length > 0) {
            r.data.datasets[0].backgroundColor = "rgba(25,118,210,0.75)";
            r.data.datasets[0].borderColor = "#1565C0";
            r.data.datasets[0].borderRadius = 6;
            r.data.datasets[0].borderWidth = 0;
            this.chartData = r.data;
            this.chartReady = true;
          }
        })
        .catch((e) => console.log(e));
    },
  },
};
</script>

<style scoped>
/* ── Page ── */
.dashboard-page {
  padding-bottom: 60px;
}

/* ── Hero ── */
.hero-card {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #1565C0 0%, #1976D2 50%, #0D47A1 100%);
  border-radius: 20px;
  padding: 28px 28px;
  color: #fff;
}

.hero-orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}
.hero-orb-1 {
  width: 220px; height: 220px;
  background: rgba(255,255,255,0.06);
  top: -60px; right: -40px;
}
.hero-orb-2 {
  width: 140px; height: 140px;
  background: rgba(255,255,255,0.04);
  bottom: -50px; left: 60px;
}

.greeting-pill {
  display: inline-flex;
  align-items: center;
  background: rgba(255,255,255,0.15);
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  backdrop-filter: blur(4px);
}

.hero-name {
  font-size: clamp(1.3rem, 4vw, 1.9rem);
  font-weight: 900;
  letter-spacing: -0.5px;
  color: #fff;
  text-shadow: 0 2px 12px rgba(0,0,0,0.15);
}

.hero-sub {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.75);
  font-weight: 500;
}

.hero-clock {
  text-align: right;
}
.clock-time {
  font-size: clamp(1.5rem, 5vw, 2.2rem);
  font-weight: 900;
  letter-spacing: 2px;
  color: #fff;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 2px 8px rgba(0,0,0,0.2);
}
.clock-date {
  font-size: 0.72rem;
  color: rgba(255,255,255,0.7);
  text-transform: capitalize;
  font-weight: 500;
  margin-top: 2px;
}

/* ── KPI Cards ── */
.kpi-card {
  position: relative;
  overflow: hidden;
  background: rgba(255,255,255,0.72) !important;
  backdrop-filter: blur(12px) !important;
  border: 1px solid rgba(255,255,255,0.35) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.10) !important;
}
.theme--dark .kpi-card {
  background: rgba(30,30,46,0.75) !important;
  border: 1px solid rgba(255,255,255,0.08) !important;
}

.kpi-accent-bar {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  border-radius: 0;
}

.kpi-icon-wrap {
  width: 32px; height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-value {
  font-size: clamp(1rem, 3vw, 1.35rem);
  font-weight: 900;
  letter-spacing: -0.5px;
  line-height: 1.2;
  margin-top: 4px;
}

.kpi-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #9e9e9e;
  margin-top: 2px;
}

/* ── Glass Card ── */
.glass-card {
  background: rgba(255,255,255,0.72) !important;
  backdrop-filter: blur(12px) !important;
  border: 1px solid rgba(255,255,255,0.35) !important;
}
.theme--dark .glass-card {
  background: rgba(30,30,46,0.75) !important;
  border: 1px solid rgba(255,255,255,0.08) !important;
}

/* ── Section Header ── */
.section-icon-pill {
  width: 26px; height: 26px;
  background: linear-gradient(135deg, #1976D2, #0D47A1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

/* ── Result Mini Cards ── */
.result-mini {
  background: rgba(25,118,210,0.05);
  border: 1px solid rgba(25,118,210,0.12);
  transition: transform 0.18s ease;
}
.result-mini:hover {
  transform: scale(1.04);
  background: rgba(25,118,210,0.09);
}
.theme--dark .result-mini {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.08);
}

.result-name {
  font-size: 0.62rem;
  letter-spacing: 0.2px;
}

.result-circle {
  width: 40px; height: 40px;
  background: linear-gradient(135deg, #1976D2, #0D47A1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(25,118,210,0.35);
}

.result-num {
  color: #fff;
  font-size: 1rem;
  font-weight: 900;
}

/* ── Quick Links ── */
.quick-link-item {
  height: 44px;
  background: rgba(25,118,210,0.05);
  border: 1px solid rgba(25,118,210,0.10);
  cursor: pointer;
  transition: all 0.18s ease;
  color: inherit;
}
.quick-link-item:hover {
  background: rgba(25,118,210,0.10);
  transform: translateX(3px);
  border-color: rgba(25,118,210,0.20);
}
.theme--dark .quick-link-item {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.08);
}
.theme--dark .quick-link-item:hover {
  background: rgba(255,255,255,0.08);
}
.quick-link-icon-wrap {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.quick-link-label {
  font-size: 0.82rem;
  letter-spacing: 0;
  color: inherit;
}

/* ── Utilities ── */
.text-xxs { font-size: 0.62rem !important; }

.animate-fade-in {
  animation: fadeInUp 0.45s ease-out;
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Responsive ── */
@media (max-width: 600px) {
  .hero-card { padding: 20px 18px; }
  .clock-time { font-size: 1.4rem; letter-spacing: 1px; }
  .hero-clock { text-align: left; }
}
</style>